// SubjectTabs/ExamsTab.js - Exams and grades management with real API
import React, { useState, useEffect } from 'react';
import {
  Paper, Typography, Button, Grid, Card, CardContent, Stack,
  IconButton, Chip, Box, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Select, FormControl,
  InputLabel, Avatar, Divider, Alert, LinearProgress, CircularProgress,
  Autocomplete, Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import QuizIcon from '@mui/icons-material/Quiz';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import TimerIcon from '@mui/icons-material/Timer';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useDispatch, useSelector } from 'react-redux';
import { useToast } from '../../../context/ToastContext';
import {
  fetchExams,
  createExam,
  updateExam,
  deleteExam,
  fetchExamResults,
  createExamResult,
  updateExamResult,
  deleteExamResult
} from '../../../slices/academicsSlice';
import { fetchAcademicTerms } from '../../../slices/academicsSlice';
import { fetchStudents } from '../../../slices/studentsSlice';
import { fetchClassById } from '../../../slices/classesSlice';

const ExamsTab = ({ subject, selectedClass, subjectColor }) => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const [openDialog, setOpenDialog] = useState(false);
  const [editingExam, setEditingExam] = useState(null);
  const [selectedExam, setSelectedExam] = useState(null);
  const [resultsDialogOpen, setResultsDialogOpen] = useState(false);
  const [studentResults, setStudentResults] = useState({});
  const [savingStudentId, setSavingStudentId] = useState(null);
  const [savingAll, setSavingAll] = useState(false);
  
  const { exams, examResults, academicTerms, loading, saving } = useSelector((state) => ({
    exams: state.academics.exams || [],
    examResults: state.academics.examResults || [],
    academicTerms: state.academics.academicTerms || [],
    loading: state.academics.loading,
    saving: state.academics.saving
  }));

  const { selectedClass: classData } = useSelector((state) => ({
    selectedClass: state.classes.selectedClass
  }));

  const [formData, setFormData] = useState({
    exam_name: '',
    exam_type: 'other',
    exam_date: '',
    total_marks: 100,
    passing_marks: '',
    weight: 1.0,
    academic_term: '',
    notes: ''
  });

  useEffect(() => {
    if (subject?.id && selectedClass?.id) {
      loadExams();
      loadAcademicTerms();
      loadClassData();
    }
  }, [subject?.id, selectedClass?.id]);

  const loadClassData = () => {
    if (selectedClass?.id) {
      dispatch(fetchClassById(selectedClass.id));
    }
  };

  const loadExams = () => {
    dispatch(fetchExams({ subject: subject.id, school_class: selectedClass.id }));
  };

  const loadAcademicTerms = () => {
    dispatch(fetchAcademicTerms());
  };

  const examTypes = {
    quiz: { label: 'اختبار سريع', color: 'info' },
    midterm: { label: 'اختبار نصفي', color: 'warning' },
    final: { label: 'اختبار نهائي', color: 'error' },
    monthly: { label: 'اختبار شهري', color: 'primary' },
    unit: { label: 'اختبار وحدة', color: 'success' },
    other: { label: 'أخرى', color: 'default' }
  };

  const handleOpenDialog = (exam = null) => {
    if (exam) {
      setEditingExam(exam);
      setFormData({
        exam_name: exam.exam_name || '',
        exam_type: exam.exam_type || 'other',
        exam_date: exam.exam_date || '',
        total_marks: exam.total_marks || 100,
        passing_marks: exam.passing_marks || '',
        weight: exam.weight || 1.0,
        academic_term: exam.academic_term || '',
        notes: exam.notes || ''
      });
    } else {
      setEditingExam(null);
      setFormData({
        exam_name: '',
        exam_type: 'other',
        exam_date: '',
        total_marks: 100,
        passing_marks: '',
        weight: 1.0,
        academic_term: '',
        notes: ''
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingExam(null);
  };

  const handleSaveExam = async () => {
    try {
      const examData = {
        subject: subject.id,
        school_class: selectedClass.id,
        exam_name: formData.exam_name,
        exam_type: formData.exam_type,
        exam_date: formData.exam_date,
        total_marks: parseFloat(formData.total_marks),
        passing_marks: formData.passing_marks ? parseFloat(formData.passing_marks) : null,
        weight: parseFloat(formData.weight),
        academic_term: formData.academic_term || null,
        notes: formData.notes || ''
      };

      if (editingExam) {
        await dispatch(updateExam({ id: editingExam.id, ...examData })).unwrap();
        showToast('✅ تم تحديث الاختبار بنجاح');
      } else {
        await dispatch(createExam(examData)).unwrap();
        showToast('✅ تم إضافة الاختبار بنجاح');
      }
      
      handleCloseDialog();
      loadExams();
    } catch (error) {
      showToast('❌ فشل في حفظ الاختبار');
    }
  };

  const handleDeleteExam = async (examId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذا الاختبار؟')) {
      return;
    }
    
    try {
      await dispatch(deleteExam(examId)).unwrap();
      showToast('✅ تم حذف الاختبار بنجاح');
      loadExams();
    } catch (error) {
      showToast('❌ فشل في حذف الاختبار');
    }
  };

  const handleViewResults = async (exam) => {
    setSelectedExam(exam);
    try {
      await dispatch(fetchExamResults(exam.id)).unwrap();
      
      // Initialize student results with existing data
      const existingResults = examResults.filter(r => r.exam === exam.id || r.exam_id === exam.id);
      const resultsMap = {};
      
      // Get students from class - filter only active (non-archived) students
      const allStudents = classData?.students || [];
      const students = allStudents.filter(student => !student.is_archived && !student.isArchived);
      
      if (students.length === 0) {
        showToast('⚠️ لا يوجد طلاب نشطين في هذا الصف');
        return;
      }
      
      students.forEach(student => {
        const existing = existingResults.find(r => r.student === student.id || r.student_id === student.id);
        resultsMap[student.id] = {
          student_id: student.id,
          student_name: `${student.first_name} ${student.second_name || ''}`.trim(),
          obtained_marks: existing?.obtained_marks || '',
          is_absent: existing?.is_absent || false,
          notes: existing?.notes || '',
          result_id: existing?.id || null
        };
      });
      
      setStudentResults(resultsMap);
      setResultsDialogOpen(true);
    } catch (error) {
      showToast('❌ فشل في جلب نتائج الاختبار');
    }
  };

  const handleSaveStudentResult = async (studentId) => {
    // Prevent multiple simultaneous saves for the same student
    if (savingStudentId === studentId || savingAll) {
      return;
    }

    const result = studentResults[studentId];
    if (!result) return;

    // Validate: if not absent, must have marks
    if (!result.is_absent && (!result.obtained_marks || parseFloat(result.obtained_marks) < 0)) {
      showToast('❌ يرجى إدخال الدرجة المحصلة');
      return;
    }

    setSavingStudentId(studentId);

    try {
      const resultData = {
        exam: selectedExam.id,
        student: studentId,
        obtained_marks: result.is_absent ? null : parseFloat(result.obtained_marks),
        is_absent: result.is_absent,
        notes: result.notes || ''
      };

      if (result.result_id) {
        await dispatch(updateExamResult({ id: result.result_id, ...resultData })).unwrap();
        showToast('✅ تم تحديث النتيجة بنجاح');
      } else {
        await dispatch(createExamResult(resultData)).unwrap();
        showToast('✅ تم إضافة النتيجة بنجاح');
      }
      
      // Reload exam results and update local state (only once)
      const updatedResults = await dispatch(fetchExamResults(selectedExam.id)).unwrap();
      loadExams();
      
      // Refresh the results dialog - filter only active students
      const resultsMap = {};
      const activeStudents = (classData?.students || []).filter(s => !s.is_archived && !s.isArchived);
      activeStudents.forEach(student => {
        const existing = updatedResults.find(r => r.student === student.id || r.student_id === student.id);
        resultsMap[student.id] = {
          student_id: student.id,
          student_name: `${student.first_name} ${student.second_name || ''}`.trim(),
          obtained_marks: existing?.obtained_marks || '',
          is_absent: existing?.is_absent || false,
          notes: existing?.notes || '',
          result_id: existing?.id || null
        };
      });
      setStudentResults(resultsMap);
    } catch (error) {
      showToast('❌ فشل في حفظ النتيجة');
    } finally {
      setSavingStudentId(null);
    }
  };

  const handleSaveAllResults = async () => {
    // Prevent multiple simultaneous saves
    if (savingAll || savingStudentId) {
      return;
    }

    const studentsToSave = Object.keys(studentResults).filter(studentId => {
      const result = studentResults[studentId];
      // Only save if there's data to save (marks entered or absent marked)
      return result && (
        result.result_id || // Has existing result
        (result.obtained_marks && !result.is_absent) || // Has marks and not absent
        result.is_absent // Is marked as absent
      );
    });

    if (studentsToSave.length === 0) {
      showToast('❌ لا توجد نتائج لحفظها');
      return;
    }

    setSavingAll(true);

    try {
      let savedCount = 0;
      const savePromises = [];

      for (const studentId of studentsToSave) {
        const result = studentResults[studentId];
        if (!result) continue;

        // Skip if no data entered
        if (!result.result_id && !result.obtained_marks && !result.is_absent) {
          continue;
        }

        // Validate: if not absent, must have marks
        if (!result.is_absent && (!result.obtained_marks || parseFloat(result.obtained_marks) < 0)) {
          continue;
        }

        const resultData = {
          exam: selectedExam.id,
          student: studentId,
          obtained_marks: result.is_absent ? null : parseFloat(result.obtained_marks),
          is_absent: result.is_absent,
          notes: result.notes || ''
        };

        if (result.result_id) {
          savePromises.push(dispatch(updateExamResult({ id: result.result_id, ...resultData })).unwrap());
        } else {
          savePromises.push(dispatch(createExamResult(resultData)).unwrap());
        }
        savedCount++;
      }

      // Wait for all saves to complete
      await Promise.all(savePromises);

      if (savedCount > 0) {
        showToast(`✅ تم حفظ ${savedCount} نتيجة بنجاح`);
      }

      // Reload exam results once after all saves
      const updatedResults = await dispatch(fetchExamResults(selectedExam.id)).unwrap();
      loadExams();
      
      // Refresh the results dialog - filter only active students
      const resultsMap = {};
      const activeStudents = (classData?.students || []).filter(s => !s.is_archived && !s.isArchived);
      activeStudents.forEach(student => {
        const existing = updatedResults.find(r => r.student === student.id || r.student_id === student.id);
        resultsMap[student.id] = {
          student_id: student.id,
          student_name: `${student.first_name} ${student.second_name || ''}`.trim(),
          obtained_marks: existing?.obtained_marks || '',
          is_absent: existing?.is_absent || false,
          notes: existing?.notes || '',
          result_id: existing?.id || null
        };
      });
      setStudentResults(resultsMap);
    } catch (error) {
      showToast('❌ فشل في حفظ بعض النتائج');
    } finally {
      setSavingAll(false);
    }
  };

  const handleDeleteStudentResult = async (resultId) => {
    if (!window.confirm('هل أنت متأكد من حذف هذه النتيجة؟')) {
      return;
    }
    
    try {
      await dispatch(deleteExamResult(resultId)).unwrap();
      showToast('✅ تم حذف النتيجة بنجاح');
      await dispatch(fetchExamResults(selectedExam.id)).unwrap();
      loadExams();
    } catch (error) {
      showToast('❌ فشل في حذف النتيجة');
    }
  };

  const handleUpdateStudentResult = (studentId, field, value) => {
    setStudentResults(prev => ({
      ...prev,
      [studentId]: {
        ...prev[studentId],
        [field]: value
      }
    }));
  };

  const getExamIcon = (type) => {
    switch (type) {
      case 'quiz': return <QuizIcon />;
      case 'midterm': return <AssessmentIcon />;
      case 'final': return <TimerIcon />;
      default: return <QuizIcon />;
    }
  };

  // Filter exams for this subject
  const subjectExams = exams.filter(exam => exam.subject === subject.id || exam.subject_id === subject.id);
  
  // Calculate statistics
  const completedExams = subjectExams.filter(e => {
    const examDate = new Date(e.exam_date);
    const today = new Date();
    return examDate < today;
  });
  
  const upcomingExams = subjectExams.filter(e => {
    const examDate = new Date(e.exam_date);
    const today = new Date();
    return examDate >= today;
  });

  // Calculate average score from results
  const calculateAverageScore = (exam) => {
    const examResultsForExam = examResults.filter(r => r.exam === exam.id || r.exam_id === exam.id);
    if (examResultsForExam.length === 0) return null;
    
    const validResults = examResultsForExam.filter(r => !r.is_absent && r.percentage !== null);
    if (validResults.length === 0) return null;
    
    const sum = validResults.reduce((acc, r) => acc + parseFloat(r.percentage || 0), 0);
    return Math.round(sum / validResults.length);
  };

  if (loading && subjectExams.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack useFlexGap direction="row" spacing={2} alignItems="center">
            <Avatar sx={{ bgcolor: subjectColor }}>
              <QuizIcon />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight="bold">
                الاختبارات والدرجات
              </Typography>
              <Typography variant="body2" color="textSecondary">
                إدارة اختبارات مادة {subject.name}
              </Typography>
            </Box>
          </Stack>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
            sx={{ 
              bgcolor: subjectColor,
              '&:hover': { bgcolor: `${subjectColor}dd` },
              borderRadius: 2
            }}
          >
            إضافة اختبار
          </Button>
        </Stack>
      </Paper>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color={subjectColor}>
                {subjectExams.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                إجمالي الاختبارات
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="success.main">
                {completedExams.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                اختبارات مكتملة
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="warning.main">
                {upcomingExams.length}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                اختبارات قادمة
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card elevation={2} sx={{ borderRadius: 3 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Typography variant="h4" fontWeight="bold" color="info.main">
                {(() => {
                  const averages = completedExams.map(e => calculateAverageScore(e)).filter(a => a !== null);
                  return averages.length > 0 
                    ? Math.round(averages.reduce((a, b) => a + b, 0) / averages.length)
                    : 0;
                })()}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                متوسط العام
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Exams List */}
      {subjectExams.length === 0 ? (
        <Paper elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: 3 }}>
          <Typography variant="h6" color="textSecondary" gutterBottom>
            لا توجد اختبارات مسجلة
          </Typography>
          <Typography variant="body2" color="textSecondary">
            ابدأ بإضافة اختبار جديد
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {subjectExams.map((exam) => {
            const isCompleted = new Date(exam.exam_date) < new Date();
            const avgScore = calculateAverageScore(exam);
            const resultsCount = examResults.filter(r => r.exam === exam.id || r.exam_id === exam.id).length;
            
            return (
              <Grid item xs={12} md={6} lg={4} key={exam.id}>
                <Card 
                  elevation={2}
                  sx={{ 
                    borderRadius: 3,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4
                    },
                    border: isCompleted ? '2px solid #4caf50' : '2px solid #ff9800'
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack spacing={2}>
                      {/* Exam Header */}
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                        <Stack useFlexGap direction="row" spacing={2} alignItems="center" sx={{ flex: 1 }}>
                          <Avatar sx={{ 
                            bgcolor: isCompleted ? 'success.light' : 'warning.light',
                            color: isCompleted ? 'success.dark' : 'warning.dark'
                          }}>
                            {getExamIcon(exam.exam_type)}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" fontWeight="bold" noWrap>
                              {exam.exam_name}
                            </Typography>
                            <Chip
                              size="small"
                              label={examTypes[exam.exam_type]?.label || 'أخرى'}
                              color={examTypes[exam.exam_type]?.color || 'default'}
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        </Stack>
                        
                        <Stack useFlexGap direction="row" spacing={0.5}>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleViewResults(exam)}
                            title="عرض النتائج"
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="primary"
                            onClick={() => handleOpenDialog(exam)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton 
                            size="small" 
                            color="error"
                            onClick={() => handleDeleteExam(exam.id)}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </Stack>

                      <Divider />

                      {/* Exam Details */}
                      <Stack spacing={1.5}>
                        <Stack useFlexGap direction="row" spacing={1} alignItems="center">
                          <CalendarTodayIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {new Date(exam.exam_date).toLocaleDateString('en-GB')}
                          </Typography>
                        </Stack>

                        <Stack useFlexGap direction="row" spacing={1} alignItems="center">
                          <AssessmentIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2">
                            {exam.total_marks} درجة
                          </Typography>
                        </Stack>

                        {exam.academic_term_name && (
                          <Typography variant="body2" color="textSecondary">
                            الفصل: {exam.academic_term_name}
                          </Typography>
                        )}
                      </Stack>

                      {/* Progress */}
                      {isCompleted && avgScore !== null ? (
                        <Box>
                          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                            <Typography variant="body2" color="textSecondary">
                              متوسط الدرجات
                            </Typography>
                            <Typography variant="body2" fontWeight="bold" color="success.main">
                              {avgScore}%
                            </Typography>
                          </Stack>
                          <LinearProgress 
                            variant="determinate" 
                            value={avgScore} 
                            sx={{ 
                              height: 8, 
                              borderRadius: 4,
                              bgcolor: 'grey.200',
                              '& .MuiLinearProgress-bar': {
                                bgcolor: avgScore >= 70 ? 'success.main' : 
                                       avgScore >= 50 ? 'warning.main' : 'error.main'
                              }
                            }} 
                          />
                        </Box>
                      ) : isCompleted ? (
                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                          <Typography variant="body2">
                            لا توجد نتائج مسجلة
                          </Typography>
                        </Alert>
                      ) : (
                        <Alert 
                          severity="info" 
                          icon={<WarningIcon />}
                          sx={{ borderRadius: 2 }}
                        >
                          <Typography variant="body2">
                            لم يبدأ الاختبار بعد
                          </Typography>
                        </Alert>
                      )}

                      {/* Results Count */}
                      {resultsCount > 0 && (
                        <Box sx={{ 
                          p: 2, 
                          bgcolor: 'grey.50', 
                          borderRadius: 2,
                          border: '1px solid',
                          borderColor: 'grey.200'
                        }}>
                          <Stack direction="row" justifyContent="space-between" alignItems="center">
                            <Typography variant="body2" color="textSecondary">
                              عدد النتائج المسجلة
                            </Typography>
                            <Typography variant="body2" fontWeight="bold">
                              {resultsCount}
                            </Typography>
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}

          {/* Add New Exam Card */}
          <Grid item xs={12} md={6} lg={4}>
            <Card 
              elevation={1}
              sx={{ 
                borderRadius: 3,
                border: '2px dashed',
                borderColor: 'grey.300',
                cursor: 'pointer',
                '&:hover': {
                  borderColor: subjectColor,
                  bgcolor: `${subjectColor}05`
                }
              }}
              onClick={() => handleOpenDialog()}
            >
              <CardContent sx={{ 
                py: 6, 
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 280
              }}>
                <Avatar sx={{ 
                  bgcolor: `${subjectColor}20`, 
                  color: subjectColor,
                  width: 64, 
                  height: 64, 
                  mb: 2 
                }}>
                  <AddIcon fontSize="large" />
                </Avatar>
                <Typography variant="h6" fontWeight="bold" color={subjectColor}>
                  إضافة اختبار جديد
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  انقر لإنشاء اختبار جديد
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Add/Edit Exam Dialog */}
      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, direction: 'rtl' }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          fontWeight: 'bold',
          background: `linear-gradient(135deg, ${subjectColor} 0%, ${subjectColor}dd 100%)`,
          color: 'white',
          m: -3,
          mb: 2,
          borderRadius: '12px 12px 0 0'
        }}>
          <Stack useFlexGap direction="row" spacing={2} alignItems="center" justifyContent="center">
            <QuizIcon />
            <Typography variant="h6">
              {editingExam ? 'تعديل الاختبار' : 'إضافة اختبار جديد'}
            </Typography>
          </Stack>
        </DialogTitle>
        
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            <TextField
              label="عنوان الاختبار"
              value={formData.exam_name}
              onChange={(e) => setFormData({...formData, exam_name: e.target.value})}
              fullWidth
              required
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
            
            <FormControl fullWidth>
              <InputLabel>نوع الاختبار</InputLabel>
              <Select
                value={formData.exam_type}
                onChange={(e) => setFormData({...formData, exam_type: e.target.value})}
                label="نوع الاختبار"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="quiz">اختبار سريع</MenuItem>
                <MenuItem value="midterm">اختبار نصفي</MenuItem>
                <MenuItem value="final">اختبار نهائي</MenuItem>
                <MenuItem value="monthly">اختبار شهري</MenuItem>
                <MenuItem value="unit">اختبار وحدة</MenuItem>
                <MenuItem value="other">أخرى</MenuItem>
              </Select>
            </FormControl>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="التاريخ"
                  type="date"
                  value={formData.exam_date}
                  onChange={(e) => setFormData({...formData, exam_date: e.target.value})}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="إجمالي الدرجات"
                  type="number"
                  value={formData.total_marks}
                  onChange={(e) => setFormData({...formData, total_marks: e.target.value})}
                  fullWidth
                  required
                  inputProps={{ min: 1, step: 0.01 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <TextField
                  label="الدرجة المطلوبة للنجاح (اختياري)"
                  type="number"
                  value={formData.passing_marks}
                  onChange={(e) => setFormData({...formData, passing_marks: e.target.value})}
                  fullWidth
                  inputProps={{ min: 0, step: 0.01 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="الوزن في المعدل"
                  type="number"
                  value={formData.weight}
                  onChange={(e) => setFormData({...formData, weight: e.target.value})}
                  fullWidth
                  inputProps={{ min: 0.1, step: 0.1 }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>

            <Autocomplete
              options={academicTerms}
              getOptionLabel={(option) => option.name || ''}
              value={academicTerms.find(t => t.id === formData.academic_term) || null}
              onChange={(event, newValue) => {
                setFormData({...formData, academic_term: newValue?.id || ''});
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="الفصل الدراسي (اختياري)"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              )}
            />

            <TextField
              label="ملاحظات (اختياري)"
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              fullWidth
              multiline
              rows={3}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Stack>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 1 }}>
          <Button 
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ minWidth: 100, borderRadius: 2 }}
          >
            إلغاء
          </Button>
          <Button 
            onClick={handleSaveExam}
            variant="contained"
            disabled={saving || !formData.exam_name || !formData.exam_date}
            sx={{ 
              minWidth: 100,
              borderRadius: 2,
              bgcolor: subjectColor,
              '&:hover': { bgcolor: `${subjectColor}dd` }
            }}
          >
            {saving ? 'جاري الحفظ...' : editingExam ? 'تحديث' : 'إضافة'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Results Dialog - Student Results Management */}
      <Dialog
        open={resultsDialogOpen}
        onClose={() => {
          setResultsDialogOpen(false);
          setStudentResults({});
        }}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: { borderRadius: 3, direction: 'rtl' }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          fontWeight: 'bold',
          background: `linear-gradient(135deg, ${subjectColor} 0%, ${subjectColor}dd 100%)`,
          color: 'white',
          m: -3,
          mb: 2,
          borderRadius: '12px 12px 0 0',
          p: 3
        }}>
          <Stack useFlexGap direction="row" spacing={2} alignItems="center" justifyContent="center">
            <AssessmentIcon />
            <Typography variant="h6">
              إدارة نتائج الاختبار: {selectedExam?.exam_name}
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
            إجمالي الدرجات: {selectedExam?.total_marks} | التاريخ: {selectedExam?.exam_date ? new Date(selectedExam.exam_date).toLocaleDateString('en-GB') : ''}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {(() => {
            const activeStudents = (classData?.students || []).filter(s => !s.is_archived && !s.isArchived);
            if (!activeStudents || activeStudents.length === 0) {
              return (
                <Alert severity="info">
                  لا يوجد طلاب نشطين في هذا الصف
                </Alert>
              );
            }
            
            return (
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 'bold' }}>اسم الطالب</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>الدرجة المحصلة</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>النسبة المئوية</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>الحالة</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 'bold' }}>إجراءات</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeStudents.map((student) => {
                      const result = studentResults[student.id];
                      const studentName = `${student.first_name} ${student.second_name || ''}`.trim();
                      const obtainedMarks = result?.obtained_marks || '';
                      const isAbsent = result?.is_absent || false;
                      const percentage = obtainedMarks && selectedExam?.total_marks 
                        ? ((parseFloat(obtainedMarks) / parseFloat(selectedExam.total_marks)) * 100).toFixed(1)
                        : null;
                      
                      return (
                        <TableRow key={student.id} hover>
                        <TableCell>
                          <Typography variant="body1" fontWeight="medium">
                            {studentName}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {result?.result_id ? (
                            <TextField
                              type="number"
                              size="small"
                              value={obtainedMarks}
                              onChange={(e) => handleUpdateStudentResult(student.id, 'obtained_marks', e.target.value)}
                              disabled={isAbsent}
                              inputProps={{ 
                                min: 0, 
                                max: selectedExam?.total_marks,
                                step: 0.01
                              }}
                              sx={{ width: 100 }}
                            />
                          ) : (
                            <TextField
                              type="number"
                              size="small"
                              placeholder="أدخل الدرجة"
                              value={obtainedMarks}
                              onChange={(e) => handleUpdateStudentResult(student.id, 'obtained_marks', e.target.value)}
                              disabled={isAbsent}
                              inputProps={{ 
                                min: 0, 
                                max: selectedExam?.total_marks,
                                step: 0.01
                              }}
                              sx={{ width: 100 }}
                            />
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {percentage ? (
                            <Chip
                              label={`${percentage}%`}
                              color={
                                parseFloat(percentage) >= 70 ? 'success' :
                                parseFloat(percentage) >= 50 ? 'warning' : 'error'
                              }
                              size="small"
                            />
                          ) : (
                            <Typography variant="body2" color="textSecondary">
                              -
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <FormControl size="small">
                            <Select
                              value={isAbsent ? 'absent' : 'present'}
                              onChange={(e) => handleUpdateStudentResult(student.id, 'is_absent', e.target.value === 'absent')}
                              sx={{ minWidth: 100 }}
                            >
                              <MenuItem value="present">حاضر</MenuItem>
                              <MenuItem value="absent">غائب</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell align="center">
                          <Stack useFlexGap direction="row" spacing={1} justifyContent="center" alignItems="center">
                            <Button
                              variant="contained"
                              size="small"
                              color="primary"
                              startIcon={savingStudentId === student.id ? <CircularProgress size={16} /> : <SaveIcon />}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleSaveStudentResult(student.id);
                              }}
                              disabled={
                                savingAll || 
                                savingStudentId === student.id ||
                                (!isAbsent && (!obtainedMarks || parseFloat(obtainedMarks) < 0 || parseFloat(obtainedMarks) > parseFloat(selectedExam?.total_marks || 0)))
                              }
                              sx={{ 
                                minWidth: 100,
                                borderRadius: 2,
                                textTransform: 'none',
                                fontSize: '0.75rem'
                              }}
                            >
                              {savingStudentId === student.id ? 'جاري الحفظ...' : 'حفظ'}
                            </Button>
                            {result?.result_id && (
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteStudentResult(result.result_id)}
                                title="حذف النتيجة"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </Stack>
                        </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            );
          })()}
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 1, justifyContent: 'space-between' }}>
          <Button
            variant="contained"
            color="success"
            startIcon={savingAll ? <CircularProgress size={20} sx={{ color: 'white' }} /> : <CheckCircleIcon />}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleSaveAllResults();
            }}
            disabled={savingAll || savingStudentId !== null}
            sx={{ 
              minWidth: 150,
              borderRadius: 2,
              bgcolor: 'success.main',
              '&:hover': { bgcolor: 'success.dark' },
              '&:disabled': { bgcolor: 'success.light' }
            }}
          >
            {savingAll ? 'جاري الحفظ...' : 'حفظ الكل'}
          </Button>
          <Button 
            onClick={() => {
              setResultsDialogOpen(false);
              setStudentResults({});
            }}
            variant="outlined"
            sx={{ minWidth: 100, borderRadius: 2 }}
          >
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExamsTab;
